import { Link } from "react-router-dom";
import { AlertTriangle, ArrowUpRight, ShieldCheck, MessageSquare, Flame } from "lucide-react";
import { scoreColor } from "@/lib/ui";

const isHotDebate = (topic) => {
  const votes = (topic.votes?.a || 0) + (topic.votes?.b || 0);
  return (topic.comment_count || 0) >= 3 || votes >= 10;
};

export const TopicCard = ({ topic, large = false, index = 0 }) => {
  const color = scoreColor(topic.controversy_score);
  return (
    <Link
      to={`/topic/${topic.id}`}
      data-testid={`topic-card-${topic.id}`}
      style={{ animationDelay: `${index * 60}ms` }}
      className={`group fade-up panel relative flex flex-col justify-between overflow-hidden p-6 transition-all duration-200 hover:-translate-y-1 hover:border-[#D92D20]/50 ${
        large ? "md:col-span-2 md:row-span-2 md:p-8" : ""
      }`}
    >
      <div className="grid-noise pointer-events-none absolute inset-0 opacity-[0.07]" />
      {large && (
        <>
          <div
            className="pointer-events-none absolute inset-0 bg-cover bg-center opacity-25"
            style={{
              backgroundImage:
                "url(https://images.pexels.com/photos/10325707/pexels-photo-10325707.png?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)",
            }}
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/85 to-transparent" />
        </>
      )}
      <div className="relative">
        <div className="mb-4 flex items-center justify-between">
          <span className="meta-label border border-[#333] px-2 py-1 text-[#A3A3A3]">
            {topic.category}
          </span>
          <div className="flex items-center gap-2">
            {isHotDebate(topic) && (
              <span
                data-testid={`hot-badge-${topic.id}`}
                className="flex items-center gap-1 border border-[#D92D20]/40 bg-[#D92D20]/10 px-1.5 py-0.5 font-mono text-[0.6rem] uppercase tracking-wider text-[#D92D20]"
              >
                <Flame className="h-3 w-3" strokeWidth={2} /> Hot
              </span>
            )}
            <AlertTriangle className="h-3.5 w-3.5" strokeWidth={1.5} style={{ color }} />
            <span className="font-mono text-xs" style={{ color }}>
              {topic.controversy_score}
            </span>
          </div>
        </div>

        <h3
          className={`font-heading tracking-tight text-white ${
            large ? "text-3xl md:text-4xl" : "text-xl"
          }`}
        >
          {topic.title}
        </h3>
        <p className={`mt-3 font-body text-[#A3A3A3] ${large ? "text-base" : "text-sm"} line-clamp-3`}>
          {topic.teaser}
        </p>
      </div>

      <div className="relative mt-6">
        <div className="mb-3 flex flex-wrap gap-2">
          {(topic.tags || []).map((t) => (
            <span key={t} className="font-mono text-[0.65rem] text-[#737373]">
              #{t}
            </span>
          ))}
        </div>
        <div className="h-px w-full bg-[#222]">
          <div className="h-px" style={{ width: `${topic.controversy_score}%`, background: color }} />
        </div>
        {(() => {
          const a = topic.votes?.a || 0;
          const b = topic.votes?.b || 0;
          const total = a + b;
          if (!total) return null;
          const pctA = Math.round((a / total) * 100);
          return (
            <div className="mt-3" data-testid={`vote-split-${topic.id}`}>
              <div className="mb-1 flex items-center justify-between font-mono text-[0.6rem] text-[#737373]">
                <span className="text-[#D92D20]">A {pctA}%</span>
                <span>{total} {total === 1 ? "vote" : "votes"}</span>
                <span className="text-[#3B82F6]">{100 - pctA}% B</span>
              </div>
              <div className="flex h-1 w-full overflow-hidden">
                <div className="h-1 bg-[#D92D20]" style={{ width: `${pctA}%` }} />
                <div className="h-1 bg-[#3B82F6]" style={{ width: `${100 - pctA}%` }} />
              </div>
            </div>
          );
        })()}
        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="meta-label text-[#737373]">
              {topic.analyzed ? "Dossier ready" : "Unsealed on open"}
            </span>
            {topic.comment_count > 0 && (
              <span
                data-testid={`comment-count-${topic.id}`}
                className="flex items-center gap-1 font-mono text-[0.65rem] text-[#737373]"
              >
                <MessageSquare className="h-3 w-3" strokeWidth={1.5} /> {topic.comment_count}
              </span>
            )}
          </div>
          {topic.verified_count > 0 ? (
            <span
              data-testid={`verified-badge-${topic.id}`}
              className="flex items-center gap-1 font-mono text-[0.65rem] uppercase tracking-wider text-[#22C55E]"
            >
              <ShieldCheck className="h-3.5 w-3.5" strokeWidth={2} /> {topic.verified_count} verified
            </span>
          ) : (
            <ArrowUpRight
              className="h-4 w-4 text-[#737373] transition-colors group-hover:text-[#D92D20]"
              strokeWidth={1.5}
            />
          )}
        </div>
      </div>
    </Link>
  );
};
